import "./FsnbBody.css";
import "./FsnbTables.css";

import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";

import Table from "components/Table/Table";
import { searchNestedIterableItem } from "core/iterable_utils";
import { useResizeHandlers } from "hooks/useResize";
import { useEstimateManager } from "pages/EstimateEditor/hooks/useEstimateManager";
import { useItemEditor } from "pages/EstimateEditor/hooks/useItemEditor";
import { useSectionEditor } from "pages/EstimateEditor/hooks/useSectionEditor";
import { changeNormInnerContentVisible, fsnbActions, getFsnbTreeExpanded, getFsnbTreeWidth } from "slices/fsnb";
import { setFetchStatus, setFetchStatusText } from "slices/local_estimate_editor";
import { setClickedTableRow } from "slices/ribbon";

import CcpMosRegionColumns from "../TableSetting/CcpMosRegionColumns";
import CoefTableColumns from "../TableSetting/CoefTableColumns";
import FsemTableColumns from "../TableSetting/FsemTableColumns";
import FsemTableColumns2022 from "../TableSetting/FsemTableColumns2022";
import FsscTableColumns from "../TableSetting/FsscTableColumns";
import FsscPgTableColumns from "../TableSetting/FsscTablePgColumns";
import KsrTableColumns from "../TableSetting/KsrTableColumns";
import NormTableColumns from "../TableSetting/NormTableColumns";
import NrspTableColumns from "../TableSetting/NrspTableColumns";
import { resourceOpenTree } from "../utils.js";
import FsnbBodyNoData from "./FsnbBodyNoData";
import FsnbCoefBox from "./FsnbCoefBox";
import FsnbNrspNormBox from "./FsnbNrspNormBox";
import FsnbPartNorm from "./FsnbPartNorm";
import FsnbTechGrpBox from "./FsnbTechGrpBox";
import FsnbTree from "./FsnbTree";
import { FsnbTreeResizer } from "./FsnbTreeResizer/FsnbTreeResizer";

const FsnbTechPartsMain = lazy(() => import("pages/Fsnb/components/FsnbTechPartsMain/FsnbTechPartsMain"));

export const FSNB_TREE_MIN_WIDTH = 185;

export default function FsnbBody(props) {
	const dispatch = useDispatch();
	const scrollBlock = props.scrollBlock;
	const clickedTableRow = useSelector((state) => state.ribbon.clickedTableRow);
	const navigateFromEditor = useSelector((state) => state.ribbon.navigateFromEditor);
	const [coefBoxVisible, setCoefBoxVisible] = useState(false);
	const [boxPartNormVisible, setBoxPartNormVisible] = useState(false);
	const [fsnbPartNormName, setFsnbPartNormName] = useState("");
	const [coefRow, setCoefRow] = useState({});

	const [nsrpNormVisible, setNsrpNormVisible] = useState(false);
	const [nsrpRow, setNsrpRow] = useState({});

	const [techGrpBoxVisible, setTechGrpBoxVisible] = useState(false);
	const [absResRow, setAbsResRow] = useState({});

	const bodyLeftWrapperRef = useRef(null);

	const currentEstmDocId = useSelector((state) => state.ribbon.currentEstmDocId);
	const currentProjectID = useSelector((state) => state.ribbon.currentProjectID);
	const editorEstmDocClickedSectionId = useSelector((state) => state.ribbon.editorEstmDocClickedSectionId);

	const treeWidth = useSelector(getFsnbTreeWidth);
	const setTreeWidth = (newValue) => dispatch(fsnbActions.setTreeWidth(newValue));
	const treeExpanded = useSelector(getFsnbTreeExpanded);
	const setTreeExpanded = (newValue) => dispatch(fsnbActions.setTreeExpanded(newValue));

	const sectionEditor = useSectionEditor({
		estmId: currentEstmDocId,
		_setFetchStatus: setFetchStatus,
		scrollBlock: {},
	});
	const itemEditor = useItemEditor({
		estmId: currentEstmDocId,
		_setFetchStatus: setFetchStatus,
		_setFetchStatusText: setFetchStatusText,
		scrollBlock: {},
	});
	const estmManager = useEstimateManager({
		estmId: currentEstmDocId,
		_setFetchStatus: setFetchStatus,
		_setFetchStatusText: setFetchStatusText,
		hooks: { sectionEditor, itemEditor, setSettingsVisible: () => {} },
	});

	useEffect(() => {
		if (!props.navigateRow?.id) return;
		const rowHeight = 22;

		const { seekItem, seekCount } = searchNestedIterableItem({
			iterable: props.tableData,
			isSeekRowCallback: (item) => item.id === props.navigateRow?.id,
			isPermittedRowCallback: () => true,
			isPermittedChildsLoopCallback: () => true,
			breakAfterFirstMatch: true,
		});

		if (seekItem) {
			scrollBlock.current.scrollTop = seekCount * rowHeight - rowHeight * 2;
			props?.setNavigateRow?.({});
		}
	}, [props.tableData, props.navigateRow?.id]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		dispatch(setClickedTableRow(null));
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	let columnsData;
	if (props.tabValue === "mat") {
		columnsData = FsscTableColumns();
	} else if (props.tabValue === "mach") {
		if (props?.selectedFsnbVers?.num >= 396) {
			// номер с которого начинаюся версии 2022
			columnsData = FsemTableColumns2022();
		} else {
			columnsData = FsemTableColumns();
		}
	} else if (props.tabValue === "norm") {
		columnsData = NormTableColumns({ inputSearchValue: props.inputSearchValue });
	} else if (props.tabValue === "pg") {
		columnsData = FsscPgTableColumns();
	} else if (props.tabValue === "ksr") {
		columnsData = KsrTableColumns();
	} else if (props.tabValue === "coef") {
		columnsData = CoefTableColumns();
	} else if (props.tabValue === "nrsp") {
		columnsData = NrspTableColumns();
	} else if (props.tabValue === "ccp") {
		columnsData = CcpMosRegionColumns();
	}

	const changeNormContentVisible = (clickedRow, contentType) => {
		dispatch(changeNormInnerContentVisible({ clickedRow, contentType }));
	};

	props.lowerCallbacks.current["changeNormContentVisible"] = changeNormContentVisible;

	const showCoefs = (row) => {
		setCoefRow(row);
		setCoefBoxVisible(true);
	};

	const showPartNorm = (row) => {
		setCoefRow(row);
		setBoxPartNormVisible(true);
		setFsnbPartNormName(`${clickedTableRow?.code} ${clickedTableRow?.name}`);
	};

	props.lowerCallbacks.current["showCoefs"] = showCoefs;
	props.lowerCallbacks.current["showPartNorm"] = showPartNorm;

	const showTechGroup = (row) => {
		setAbsResRow(row);
		setTechGrpBoxVisible(true);
	};

	const showConnectedNorm = (row) => {
		setNsrpRow(row);
		setNsrpNormVisible(true);
	};

	const getMainTable = ({ className }) => {
		if (props.tableData.length && columnsData != null) {
			return (
				<Table
					rowData={props.tableData}
					columnsData={columnsData}
					scrollBlock={scrollBlock}
					options={{
						selectOptions: {
							controlled: true,
							uncontrolled: false,
							multiple: false,
							expandOnSelect: {
								columnsName: ["name"],
								fontSize: 12,
								tableClass: className,
							},
							preventSelectColumns: ["show_fsnb_content", "show_fsnb_resource"],
						},
						displayOptions: {
							displayScrollDelay: 20,
							targetRowHeight: 17,
							newResizableColumns: true,
							columnsWithMinWidth: [{ name: "name", minWidth: "200px" }],
						},
					}}
					funcs={{
						setTableData: ({ newTableData }) => props.f_setTableData(newTableData),
						customFuncs: {
							showCoefs,
							showTechGroup,
							showConnectedNorm,
							changeNormContentVisible,
						},
						doubleClickRowAction: {
							action: (rowObj, e) => {
								if (navigateFromEditor === "add") {
									const rowObj = { model_name: "LocalEstimatesItems", section_id: editorEstmDocClickedSectionId };
									const dataObj = { code: `${clickedTableRow.prefix || ""}${clickedTableRow.code}` };
									const clmName = "";
									estmManager.editorFactory({
										rowObj,
										dataObj,
										clmName,
										thenNavigate: {
											path: `/project/${currentProjectID}/editor/lsr/${currentEstmDocId}/`,
										},
										thenScroll: true,
									});
								} else if (rowObj?.row_type_style === "fake_parent") {
									props.f_handleTreeClick(rowObj);
									props.f_setTreeData((prev) => [...resourceOpenTree(prev, { key: rowObj.id }, 0)[0]]);
									props.f_setSelectedTreeData({ key: rowObj.id });
								} else {
									if (props.tabValue === "norm" && props.searchMode === "flat") {
										props.setInputSearchValue("");
									}
								}
							},
						},
					}}
				/>
			);
		} else if (props.tabValue === "coef") {
			return;
		} else {
			return <FsnbBodyNoData tabValue={props.tabValue} />;
		}
	};

	const {
		toggleHandler: treeToggleHandler,
		saveWidthHandler: saveTreeWidth,
		initialWidthHandler: getInitialSummaryTreeWidth,
	} = useResizeHandlers({
		resizableElem: bodyLeftWrapperRef.current,
		expanded: treeExpanded,
		setExpanded: setTreeExpanded,
		minWidth: FSNB_TREE_MIN_WIDTH,
		width: treeWidth,
		setWidth: setTreeWidth,
		settingsPageName: "fsnb",
		settingExpandedKey: "treeExpanded",
		settingWidthKey: "treeWidth",
	});

	return (
		<div className="FsnbBody">
			{props.searchMode !== "flat" && (
				<div className="FsnbBodyLeft">
					<div ref={bodyLeftWrapperRef} className="FsnbBodyLeftWrapper" style={{ width: getInitialSummaryTreeWidth() }}>
						<div className="FsnbBodyLeftIcon">
							<Tooltip title={treeExpanded ? "Свернуть" : "Развернуть"} onClick={treeToggleHandler}>
								<IconButton size="small" sx={{ padding: "2px" }}>
									{treeExpanded ? <ChevronLeft sx={{ fontSize: "17px" }} /> : <ChevronRight sx={{ fontSize: "17px" }} />}
								</IconButton>
							</Tooltip>
						</div>
						{props.treeData?.length ? (
							<FsnbTree
								data={props.treeData}
								selected={props.selectedTreeData}
								searchObjScope={props.searchObjScope}
								f_setSelectedTreeData={props.f_setSelectedTreeData}
								f_changeChildsVisible={props.f_changeChildsVisible}
								f_setSearchObjScope={props.f_setSearchObjScope}
							/>
						) : (
							<div></div>
						)}
						<FsnbTreeResizer resizableElem={bodyLeftWrapperRef.current} minWidth={FSNB_TREE_MIN_WIDTH} saveElemWidth={saveTreeWidth} />
					</div>
				</div>
			)}

			{props.tabValue === "coef" ? (
				<div className="FsnbBodyRightWrapper">
					<div className="FsnbBodyRightCoef" ref={scrollBlock} table_type={props.tabValue}>
						{getMainTable({ className: "FsnbBodyRightCoef" })}
					</div>
				</div>
			) : (
				<div
					className="FsnbBodyRight"
					ref={scrollBlock}
					table_type={props.tabValue}
					search_mode={props.searchMode}
					onScroll={props.handleScroll}
				>
					{props.fsnbBodyContent === "table" ? (
						getMainTable({ className: "FsnbBodyRight" })
					) : (
						<Suspense fallback={" "}>
							<FsnbTechPartsMain
								fetchStatus={props.fetchStatus}
								tabValue={props.tabValue}
								pdfData={props?.pdfData}
								f_setFetchStatus={props.f_setFetchStatus}
							/>
						</Suspense>
					)}
				</div>
			)}

			{coefBoxVisible && (
				<FsnbCoefBox row={coefRow} showNrsp={true} vers={props?.selectedFsnbVers} f_closeCoefBox={() => setCoefBoxVisible(false)} />
			)}

			{boxPartNormVisible && (
				<FsnbPartNorm
					name={fsnbPartNormName}
					row={coefRow}
					showNrsp={true}
					vers={props?.selectedFsnbVers}
					f_closeCoefBox={() => {
						setBoxPartNormVisible(false);
						setFsnbPartNormName("");
					}}
				/>
			)}

			{techGrpBoxVisible && (
				<FsnbTechGrpBox row={absResRow} f_closeCoefBox={() => setTechGrpBoxVisible(false)} vers={props?.selectedFsnbVers} />
			)}
			{nsrpNormVisible && <FsnbNrspNormBox row={nsrpRow} f_closeCoefBox={() => setNsrpNormVisible(false)} vers={props?.selectedFsnbVers} />}
		</div>
	);
}
